import { useEffect, useState } from 'react';
import { updateMyProfile, redeemClassInvite } from '../api/user/account.api';
import { showToast } from '../utill/utill';

/* ─── 옵션 정의 ─── */
const FIELD_OPTIONS = [
  { value: 'student',    label: '학생',         desc: '대학생, 대학원생, 고등학생' },
  { value: 'worker',     label: '직장인',       desc: '회사원, 공무원, 프리랜서' },
  { value: 'educator',   label: '교육자/강사',  desc: '교수, 강사, 교사' },
  { value: 'developer',  label: '개발자/엔지니어', desc: '소프트웨어, IT, 데이터' },
  { value: 'researcher', label: '연구원',       desc: '학술 연구, 기업 R&D' },
  { value: 'etc',        label: '기타',         desc: '직접 입력' },
];

const AI_OPTIONS = [
  { value: 'novice',       label: '처음이에요',           desc: 'AI가 뭔지는 알지만 써본 적 없어요' },
  { value: 'occasional',   label: '가끔 사용해봤어요',    desc: 'ChatGPT 등을 몇 번 써봤어요' },
  { value: 'frequent',     label: '자주 사용하고 있어요', desc: '다양한 AI 도구를 활용하고 있어요' },
  { value: 'professional', label: '업무에 활용 중이에요', desc: '실무에서 AI를 적극 활용하고 있어요' },
];

const PURPOSE_OPTIONS = [
  { value: 'class_participation', label: '수업/교육 참여', desc: '강사의 수업에 참여하여 학습' },
  { value: 'self_study',          label: 'AI 학습/실습',   desc: 'AI 모델을 비교하며 스스로 학습' },
  { value: 'work',                label: '업무 활용',      desc: '실무에서 AI를 활용하여 생산성 향상' },
  { value: 'teaching',            label: '교육 운영/강의', desc: 'AI 교육을 직접 운영하고 관리' },
  { value: 'etc',                 label: '기타',           desc: '직접 입력' },
];

const STEP_TITLE = {
  1: '어떤 분야에서 활동하고 계신가요?',
  2: '소속이 있으신가요?',
  3: 'AI를 얼마나 사용해보셨나요?',
  4: 'GrowFit을 어떻게 활용하실 건가요?',
};

const STEP_SUB = {
  1: '나에게 맞는 경험을 추천해드릴게요',
  2: '학교, 회사, 기관 등을 알려주세요',
  3: '경험에 맞는 가이드를 제공해드려요',
  4: '목적에 맞는 기능을 안내해드릴게요',
};

/**
 * 첫 로그인 시 강제 표시되는 정보 수집 모달 (4스텝).
 *  - Step 1 분야 → user_field
 *  - Step 2 소속 → organization
 *  - Step 3 AI 경험 → ai_experience
 *  - Step 4 사용 목적 → purpose
 *  → 완료 시 PATCH /user/my/profile + POST /user/class/invites/redeem 호출
 *  → 성공 후 부모에 onCompleted 콜백
 *
 * @param {{
 *   open: boolean,
 *   onCompleted: () => void,
 *   skipRedeem?: boolean,  // 이미 basic class에 등록되어 있으면 true → redeem 호출 생략
 * }} props
 */
export default function OnboardingModal({ open, onCompleted, skipRedeem = false }) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState({});
  const [customField, setCustomField] = useState('');
  const [customPurpose, setCustomPurpose] = useState('');
  const [showFieldEtc, setShowFieldEtc] = useState(false);
  const [showPurposeEtc, setShowPurposeEtc] = useState(false);
  const [orgInput, setOrgInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 모달이 열릴 때마다 상태 초기화
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setData({});
    setCustomField('');
    setCustomPurpose('');
    setShowFieldEtc(false);
    setShowPurposeEtc(false);
    setOrgInput('');
    setSubmitting(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const goNext = (patch) => {
    setData((prev) => ({ ...prev, ...patch }));
    setShowFieldEtc(false);
    setShowPurposeEtc(false);
    setCustomField('');
    setCustomPurpose('');
    if (step < 4) {
      setStep(step + 1);
    } else {
      submit({ ...data, ...patch });
    }
  };

  const goBack = () => {
    if (step <= 1) return;
    setStep(step - 1);
    setShowFieldEtc(false);
    setShowPurposeEtc(false);
  };

  const submit = async (finalData) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // 1) 프로필 정보 저장 (etc 정합성 유지)
      const payload = {
        user_field: finalData.user_field || null,
        user_field_etc:
          finalData.user_field === 'etc' ? (finalData.user_field_etc || '') : null,
        organization: finalData.organization || '',
        ai_experience: finalData.ai_experience || null,
        purpose: finalData.purpose || null,
        purpose_etc:
          finalData.purpose === 'etc' ? (finalData.purpose_etc || '') : null,
      };
      try {
        await updateMyProfile(payload);
      } catch (err) {
        // 프로필 저장은 실패해도 클래스 등록은 시도 (사용자 차단 방지)
        // eslint-disable-next-line no-console
        console.warn('프로필 저장 실패:', err);
      }

      // 2) basic 클래스 초대코드 redeem — 이미 등록되어 있으면 생략
      if (!skipRedeem) {
        const code = process.env.REACT_APP_BASIC_INVITE_CODE;
        if (!code) {
          showToast('초대코드 환경 변수가 설정되어 있지 않습니다.', 'error');
          setSubmitting(false);
          return;
        }
        try {
          await redeemClassInvite(code);
        } catch (err) {
          // 이미 등록된 경우 detail에 그 의미가 있을 수 있어 메시지 매핑
          const detail = err?.response?.data?.detail || err?.normalizedMessage || '';
          if (
            /already|이미|exists/i.test(String(detail)) ||
            err?.response?.status === 409
          ) {
            // 이미 등록되어 있으면 진행으로 간주
          } else {
            showToast(detail || '클래스 등록에 실패했습니다.', 'error');
            setSubmitting(false);
            return;
          }
        }
      }

      showToast('환영합니다! 설정이 완료되었습니다.', 'success');
      onCompleted?.();
    } finally {
      setSubmitting(false);
    }
  };

  const selectedField = data.user_field;
  const selectedAi = data.ai_experience;
  const selectedPurpose = data.purpose;

  return (
    <div className="onboarding-modal__overlay" role="dialog" aria-modal="true">
      <div className="onboarding-modal">
        <header className="onboarding-modal__head">
          <div className="onboarding-modal__brand" aria-hidden="true">GF</div>
          <div className="onboarding-modal__progress">
            {[1, 2, 3, 4].map((i) => (
              <span
                key={i}
                className={`onboarding-modal__dot ${
                  step === i ? 'onboarding-modal__dot--active' : ''
                } ${step > i ? 'onboarding-modal__dot--done' : ''}`}
              />
            ))}
          </div>
          <h2 className="onboarding-modal__title">{STEP_TITLE[step]}</h2>
          <p className="onboarding-modal__subtitle">{STEP_SUB[step]}</p>
        </header>

        <div className="onboarding-modal__body">
          {/* Step 1: 분야 */}
          {step === 1 && (
            <div className="onboarding-modal__grid">
              {FIELD_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`onboarding-modal__option ${
                    selectedField === opt.value
                      ? 'onboarding-modal__option--selected'
                      : ''
                  }`}
                  onClick={() => {
                    if (opt.value === 'etc') {
                      setShowFieldEtc(true);
                    } else {
                      goNext({ user_field: opt.value, user_field_etc: '' });
                    }
                  }}
                >
                  <div className="onboarding-modal__option-label">{opt.label}</div>
                  <div className="onboarding-modal__option-desc">{opt.desc}</div>
                </button>
              ))}
              {showFieldEtc && (
                <div className="onboarding-modal__etc">
                  <input
                    className="onboarding-modal__input"
                    type="text"
                    placeholder="분야를 입력해주세요"
                    value={customField}
                    onChange={(e) => setCustomField(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && customField.trim()) {
                        goNext({
                          user_field: 'etc',
                          user_field_etc: customField.trim(),
                        });
                      }
                    }}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="onboarding-modal__primary"
                    disabled={!customField.trim()}
                    onClick={() =>
                      goNext({
                        user_field: 'etc',
                        user_field_etc: customField.trim(),
                      })
                    }
                  >
                    확인
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: 소속 */}
          {step === 2 && (
            <div className="onboarding-modal__org">
              <input
                className="onboarding-modal__input"
                type="text"
                placeholder="학교, 회사, 기관명을 입력해주세요"
                value={orgInput}
                onChange={(e) => setOrgInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && orgInput.trim()) {
                    goNext({ organization: orgInput.trim() });
                  }
                }}
                autoFocus
              />
              {orgInput.trim() && (
                <button
                  type="button"
                  className="onboarding-modal__primary"
                  onClick={() => goNext({ organization: orgInput.trim() })}
                >
                  다음
                </button>
              )}
              <div className="onboarding-modal__or">또는</div>
              <button
                type="button"
                className="onboarding-modal__ghost"
                onClick={() => goNext({ organization: '없음/개인' })}
              >
                소속 없음 / 개인
              </button>
            </div>
          )}

          {/* Step 3: AI 경험 */}
          {step === 3 && (
            <div className="onboarding-modal__grid onboarding-modal__grid--cols-2">
              {AI_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`onboarding-modal__option ${
                    selectedAi === opt.value
                      ? 'onboarding-modal__option--selected'
                      : ''
                  }`}
                  onClick={() => goNext({ ai_experience: opt.value })}
                >
                  <div className="onboarding-modal__option-label">{opt.label}</div>
                  <div className="onboarding-modal__option-desc">{opt.desc}</div>
                </button>
              ))}
            </div>
          )}

          {/* Step 4: 사용 목적 */}
          {step === 4 && (
            <div className="onboarding-modal__grid">
              {PURPOSE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`onboarding-modal__option ${
                    selectedPurpose === opt.value
                      ? 'onboarding-modal__option--selected'
                      : ''
                  }`}
                  onClick={() => {
                    if (opt.value === 'etc') {
                      setShowPurposeEtc(true);
                    } else {
                      goNext({ purpose: opt.value, purpose_etc: '' });
                    }
                  }}
                  disabled={submitting}
                >
                  <div className="onboarding-modal__option-label">{opt.label}</div>
                  <div className="onboarding-modal__option-desc">{opt.desc}</div>
                </button>
              ))}
              {showPurposeEtc && (
                <div className="onboarding-modal__etc">
                  <input
                    className="onboarding-modal__input"
                    type="text"
                    placeholder="사용 목적을 입력해주세요"
                    value={customPurpose}
                    onChange={(e) => setCustomPurpose(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && customPurpose.trim()) {
                        goNext({
                          purpose: 'etc',
                          purpose_etc: customPurpose.trim(),
                        });
                      }
                    }}
                    autoFocus
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    className="onboarding-modal__primary"
                    disabled={!customPurpose.trim() || submitting}
                    onClick={() =>
                      goNext({
                        purpose: 'etc',
                        purpose_etc: customPurpose.trim(),
                      })
                    }
                  >
                    {submitting ? '저장 중...' : '완료'}
                  </button>
                </div>
              )}
              {submitting && (
                <p className="onboarding-modal__hint">설정을 저장하고 있습니다...</p>
              )}
            </div>
          )}
        </div>

        <footer className="onboarding-modal__foot">
          <button
            type="button"
            className="onboarding-modal__ghost"
            onClick={goBack}
            disabled={step <= 1 || submitting}
          >
            이전
          </button>
          <span className="onboarding-modal__step-count">{step} / 4</span>
        </footer>
      </div>
    </div>
  );
}
